import { Form, Formik } from 'formik';
import { useContext, useState } from 'react';
import useSWR from 'swr';
import { APIOrganizationWithRoleAndPlan } from '../../../../../../../../typings/Organization.interface';
import { Authenticator, MultiFactorStatusResponse } from '../../../../../typings/MultiFactor.interface';
import Alert from '../../../../common/base/Alert';
import Button, { PlaceholderButton } from '../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../common/base/Card';
import Icon from '../../../../common/base/Icon';
import Text from '../../../../common/base/Text';
import Tooltip from '../../../../common/base/Tooltip';
import { StyledSwitch } from '../../../../common/Form/Fields/SwitchInput';
import TextInput from '../../../../common/Form/Fields/TextInput';
import { Div } from '../../../../common/helpers/StyledUtils';
import Modal from '../../../../common/Modal';
import AuthenticatorSetup from '../../../../common/MultiFactor/AuthenticatorSetup';
import { BackupCodesDisplay } from '../../../../common/MultiFactor/BackupCodes';
import { useToasts } from '../../../../common/Toast';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { UserContext } from '../../../../contexts/UserContext';
import { formatDate } from '../../../../../utils/date';
import { useDatePreferences } from '../../../../hooks/useDatePreferences';
export const AccountMultiFactorAuthentication: React.FC = () => {
  const {
    timezone
  } = useDatePreferences();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    user
  } = useContext(UserContext);
  const {
    addToast
  } = useToasts();
  const {
    data: mfa_status,
    error: status_error,
    mutate: mutate_status
  } = useSWR<MultiFactorStatusResponse>('mfa_status', () => HookdeckAPI.multi_factor.getStatus(), {
    shouldRetryOnError: false,
    revalidateOnFocus: false
  });
  const {
    data: authenticators,
    error: authenticators_error,
    mutate: mutate_authenticators
  } = useSWR<Authenticator[]>('mfa_authenticators', () => HookdeckAPI.multi_factor.listAuthenticators(), {
    shouldRetryOnError: false,
    revalidateOnFocus: false
  });
  const {
    data: organizations,
    error: organizations_error
  } = useSWR<APIOrganizationWithRoleAndPlan[]>('organizations', () => HookdeckAPI.organizations.listAll(), {
    shouldRetryOnError: false,
    revalidateOnFocus: false
  });
  const is_loading = mfa_status === undefined && !status_error || authenticators === undefined && !authenticators_error || organizations === undefined && !organizations_error;
  const is_mfa_required = (organizations ?? []).some(org => org.mfa_required);
  const is_mfa_enabled = !!mfa_status?.enabled;
  const revalidateMultiFactorData = () => {
    mutate_status();
    mutate_authenticators();
  };

  // UI state
  const [show_setup, setShowSetup] = useState(false);
  const [initial_backup_codes, setInitialBackupCodes] = useState<string[] | null>(null);
  const [show_disable_modal, setShowDisableModal] = useState(false);
  const [is_disabling, setIsDisabling] = useState(false);
  const [delete_modal, setDeleteModal] = useState<Authenticator | null>(null);
  const [is_deleting, setIsDeleting] = useState(false);
  const [show_regenerate_modal, setShowRegenerateModal] = useState(false);
  const [new_codes, setNewCodes] = useState<string[] | null>(null);
  const [is_generating, setIsGenerating] = useState(false);
  const handleSetupSuccess = (backup_codes?: string[]) => {
    setShowSetup(false);
    revalidateMultiFactorData();
    if (backup_codes) {
      setInitialBackupCodes(backup_codes);
    }
  };
  const handleDisableMFA = async (password: string) => {
    setIsDisabling(true);
    try {
      await HookdeckAPI.multi_factor.disableMFA(password);
      addToast('success', 'Multi-factor authentication disabled');
      setShowDisableModal(false);
      revalidateMultiFactorData();
    } catch (e: any) {
      addToast('error', e.response?.message || 'Failed to disable multi-factor authentication');
    } finally {
      setIsDisabling(false);
    }
  };
  const handleDeleteAuthenticator = async (password_0: string) => {
    if (!delete_modal) return;
    setIsDeleting(true);
    try {
      await HookdeckAPI.multi_factor.deleteAuthenticator(delete_modal.id, password_0);
      addToast('success', 'Authenticator removed');
      setDeleteModal(null);
      revalidateMultiFactorData();
    } catch (e_0: any) {
      addToast('error', e_0.response?.data?.message || e_0.response?.message || 'Failed to remove authenticator');
    } finally {
      setIsDeleting(false);
    }
  };
  const handleRegenerate = async (password_1: string) => {
    setIsGenerating(true);
    try {
      const response = await HookdeckAPI.multi_factor.generateBackupCodes(password_1);
      setNewCodes(response.backup_codes);
      setShowRegenerateModal(false);
      revalidateMultiFactorData();
    } catch (e_1: any) {
      addToast('error', e_1.response?.message || 'Failed to generate backup codes');
    } finally {
      setIsGenerating(false);
    }
  };
  const handleSwitchChange = () => {
    if (is_mfa_enabled) {
      if (!is_mfa_required) {
        setShowDisableModal(true);
      }
    } else {
      setShowSetup(true);
    }
  };
  const is_last_authenticator = (authenticators?.length ?? 0) === 1;
  const delete_disabled = is_last_authenticator && is_mfa_required;
  return <>
      <Div m={{
      b: 10
    }}>
        <Text bold size="l" as="h2" m={{
        b: 4
      }}>
          Security
        </Text>

        <StyledCard separator>
          <StyledCardSection flex={{
          justify: 'flex-start',
          align: 'center',
          gap: 3
        }} p={{
          x: 4,
          y: 3
        }}>
            <Tooltip tooltip={is_mfa_enabled && is_mfa_required ? `You're a member of at least one organization that requires multi-factor authentication.` : undefined} disabled={!(is_mfa_enabled && is_mfa_required)}>
              <StyledSwitch has_label={false} disabled={is_loading}>
                <input type="checkbox" checked={is_mfa_enabled} disabled={is_loading || is_mfa_enabled && is_mfa_required} onChange={handleSwitchChange} />
              </StyledSwitch>
            </Tooltip>
            <Div flex={{
            align: 'center',
            gap: 1
          }}>
              <Text>Multi-factor authentication</Text>
              <Tooltip tooltip="Use authenticator apps to generate a one-time password as an additional identity verification factor for sign-in.">
                <Icon muted icon="info" />
              </Tooltip>
            </Div>
          </StyledCardSection>

          {!is_loading && is_mfa_enabled && <StyledCardSection p={4} flex={{
          direction: 'column',
          gap: 4
        }}>
              <StyledCard>
                {(authenticators || []).map((authenticator, index) => <StyledCardSection key={authenticator.id} flex={{
              align: 'center',
              justify: 'space-between'
            }} p={{
              x: 4,
              y: 3
            }}>
                    <Div>
                      <Text semi size="s">
                        MFA Method {index + 1}
                      </Text>
                      <Text size="s" muted>
                        Added{' '}
                        {formatDate(authenticator.created_at, 'MMM d, yyyy, h:mm a', timezone)}
                        {authenticator.last_used_at ? ` · Last used ${formatDate(authenticator.last_used_at, 'MMM d, yyyy h:mm a', timezone)}` : ''}
                      </Text>
                    </Div>
                    <Tooltip tooltip={delete_disabled ? `You're a member of at least one organization that requires multi-factor authentication.` : undefined} disabled={!delete_disabled}>
                      <Button outline danger icon="delete" disabled={delete_disabled} onClick={() => setDeleteModal(authenticator)}>
                        Remove
                      </Button>
                    </Tooltip>
                  </StyledCardSection>)}
              </StyledCard>
              <PlaceholderButton onClick={() => setShowSetup(true)}>
                Add authenticator app
              </PlaceholderButton>
            </StyledCardSection>}

          {!is_loading && is_mfa_enabled && <StyledCardSection muted flex={{
          align: 'center',
          justify: 'space-between'
        }} p={{
          x: 4,
          y: 3
        }}>
              <Div flex={{
            align: 'center',
            gap: 1
          }}>
                <Text>Recovery codes</Text>
                <Tooltip tooltip="Security codes that can be used if you're unable to access any of your authentication methods.">
                  <Icon muted icon="info" />
                </Tooltip>
              </Div>
              <Button outline icon="cached" onClick={() => setShowRegenerateModal(true)}>
                Regenerate
              </Button>
            </StyledCardSection>}
        </StyledCard>
      </Div>

      {show_setup && <AuthenticatorSetup onClose={() => setShowSetup(false)} onSuccess={handleSetupSuccess} has_password={user?.has_password} />}

      {initial_backup_codes && <BackupCodesDisplay codes={initial_backup_codes} onClose={() => setInitialBackupCodes(null)} />}

      {show_disable_modal && <Formik initialValues={{
      password: ''
    }} validate={values => {
      const errors: {
        password?: string;
      } = {};
      if (!values.password) {
        errors.password = 'Password is required';
      }
      return errors;
    }} onSubmit={values_0 => handleDisableMFA(values_0.password)}>
          {props => <Modal title="Disable multi-factor authentication" portal onClose={() => setShowDisableModal(false)} onCancel={() => setShowDisableModal(false)} cancel_label="Cancel" submit_label="Disable" danger is_submitting={is_disabling} submit_disabled={!props.isValid || !props.values.password} onSubmit={props.submitForm}>
              <Form>
                <Alert inline danger m={{
            b: 4
          }}>
                  <Text size="s">
                    Disabling multi-factor authentication will make your account less secure. All
                    authenticators and backup codes will be removed.
                  </Text>
                </Alert>
                <TextInput label="Confirm your password" name="password" type="password" auto_complete="current-password" required focus />
              </Form>
            </Modal>}
        </Formik>}

      {delete_modal && <Formik initialValues={{
      password: ''
    }} validate={values_1 => {
      const errors_0: {
        password?: string;
      } = {};
      if (!values_1.password) {
        errors_0.password = 'Password is required';
      }
      return errors_0;
    }} onSubmit={values_2 => handleDeleteAuthenticator(values_2.password)}>
          {props_0 => <Modal title="Remove authenticator app" portal onClose={() => setDeleteModal(null)} onCancel={() => setDeleteModal(null)} cancel_label="Cancel" submit_label="Remove" danger is_submitting={is_deleting} submit_disabled={!props_0.isValid || !props_0.values.password} onSubmit={props_0.submitForm}>
              <Form>
                {is_last_authenticator && <Alert inline danger m={{
            b: 4
          }}>
                    <Text bold>This is your only authentication method</Text>
                    <Text>
                      You have no alternative multi-factor authentication methods set up on this
                      account — when you remove this, MFA will be turned off.
                    </Text>
                  </Alert>}
                <Text m={{
            b: 4
          }}>Are you sure you want to remove this authenticator?</Text>
                <TextInput label="Confirm your password" name="password" type="password" auto_complete="current-password" required focus m={{
            b: 0
          }} />
              </Form>
            </Modal>}
        </Formik>}

      {show_regenerate_modal && <Formik initialValues={{
      password: ''
    }} validate={values_3 => {
      const errors_1: {
        password?: string;
      } = {};
      if (!values_3.password) {
        errors_1.password = 'Password is required';
      }
      return errors_1;
    }} onSubmit={values_4 => handleRegenerate(values_4.password)}>
          {props_1 => <Modal title="Regenerate backup codes" portal onClose={() => setShowRegenerateModal(false)} onCancel={() => setShowRegenerateModal(false)} cancel_label="Cancel" submit_label="Regenerate" is_submitting={is_generating} submit_disabled={!props_1.isValid || !props_1.values.password} onSubmit={props_1.submitForm}>
              <Form>
                <Alert inline warning m={{
            b: 4
          }}>
                  <Text size="s">
                    This will invalidate all existing backup codes and generate new ones.
                  </Text>
                </Alert>
                <TextInput label="Confirm your password" name="password" type="password" auto_complete="current-password" required focus />
              </Form>
            </Modal>}
        </Formik>}

      {new_codes && <BackupCodesDisplay codes={new_codes} onClose={() => setNewCodes(null)} />}
    </>;
};
export default AccountMultiFactorAuthentication;